/**
 * Platform bridge. The same React app runs as a PWA and inside the Capacitor shells (Android/iOS).
 * Native plugins are imported lazily so the web bundle doesn't pay for them.
 */
import { Capacitor } from '@capacitor/core';

export type AppPlatform = 'web' | 'android' | 'ios';

export const isNative = Capacitor.isNativePlatform();
export const platform: AppPlatform = (Capacitor.getPlatform() as AppPlatform) ?? 'web';

/** Secure-ish persistent storage for the refresh token in native apps (web uses an httpOnly cookie instead). */
const REFRESH_KEY = 'th.refresh';

export async function getStoredRefreshToken(): Promise<string | null> {
  if (!isNative) return null;
  const { Preferences } = await import('@capacitor/preferences');
  return (await Preferences.get({ key: REFRESH_KEY })).value;
}

export async function storeRefreshToken(token: string | null): Promise<void> {
  if (!isNative) return;
  const { Preferences } = await import('@capacitor/preferences');
  if (token) await Preferences.set({ key: REFRESH_KEY, value: token });
  else await Preferences.remove({ key: REFRESH_KEY });
}

export async function haptic(kind: 'light' | 'medium' | 'heavy' = 'light'): Promise<void> {
  if (isNative) {
    const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
    const style = kind === 'heavy' ? ImpactStyle.Heavy : kind === 'medium' ? ImpactStyle.Medium : ImpactStyle.Light;
    await Haptics.impact({ style }).catch(() => undefined);
  } else if ('vibrate' in navigator) {
    navigator.vibrate(kind === 'heavy' ? 60 : kind === 'medium' ? 35 : 15);
  }
}

export async function nativeShare(data: { title: string; text: string; url: string }): Promise<boolean> {
  if (!isNative) return false;
  const { Share } = await import('@capacitor/share');
  try {
    await Share.share({ title: data.title, text: data.text, url: data.url, dialogTitle: data.title });
    return true;
  } catch {
    return false;
  }
}

/**
 * One-time native setup: status bar, splash, deep links (App/Universal Links + custom scheme),
 * Android back button.
 */
export async function initNativeShell(navigate: (path: string) => void, onResume?: (awayMs: number) => void): Promise<void> {
  if (!isNative) return;
  const [{ App }, { StatusBar, Style }, { SplashScreen }] = await Promise.all([
    import('@capacitor/app'),
    import('@capacitor/status-bar'),
    import('@capacitor/splash-screen'),
  ]);
  await StatusBar.setStyle({ style: Style.Dark }).catch(() => undefined);
  if (platform === 'android') await StatusBar.setBackgroundColor({ color: '#0a0f1e' }).catch(() => undefined);
  await App.addListener('appUrlOpen', ({ url }) => {
    const path = deepLinkPath(url);
    if (path) navigate(path);
  });
  // Android/iOS freeze backgrounded apps; tell the app how long it was away so it can refresh its connection.
  let pausedAt = 0;
  await App.addListener('appStateChange', ({ isActive }) => {
    if (!isActive) pausedAt = Date.now();
    else if (pausedAt) {
      onResume?.(Date.now() - pausedAt);
      pausedAt = 0;
    }
  });
  await App.addListener('backButton', ({ canGoBack }) => {
    if (canGoBack) window.history.back();
    else void App.minimizeApp();
  });
  const launch = await App.getLaunchUrl().catch(() => undefined);
  if (launch?.url) {
    const path = deepLinkPath(launch.url);
    if (path) navigate(path);
  }
  await SplashScreen.hide().catch(() => undefined);
}

/** Maps https://<host>/join/ABC123, cardhub://join/ABC123 and /r/CODE links to in-app routes. */
export function deepLinkPath(url: string): string | null {
  try {
    const u = new URL(url);
    const path = u.protocol.startsWith('http') ? u.pathname : `/${u.host}${u.pathname}`;
    const m = /^\/(join|r)\/([A-Za-z0-9]{4,16})\/?$/.exec(path);
    return m ? `/${m[1]}/${m[2]!.toUpperCase()}` : null;
  } catch {
    return null;
  }
}
